import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QrScanner from 'qr-scanner';
import { useAppDispatch } from '../store/hooks';
import { closeScanner } from '../store/slices/navigationSlice';
import { ExhibitId } from '../store/slices/exhibitSlice';

export default function QrScannerPage() {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [scanner, setScanner] = useState<QrScanner | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [scanning, setScanning] = useState(false);
    
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    
    useEffect(() => {
        if (videoRef.current && !scanner) {
            try {
                // Initialize QR scanner
                const newScanner = new QrScanner(
                    videoRef.current,
                    result => {
                        handleScan(result.data);
                    },
                    {
                        returnDetailedScanResult: true,
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                        preferredCamera: 'environment',
                    }
                );
                
                setScanner(newScanner);
                newScanner.start().then(() => {
                    setScanning(true);
                }).catch(err => {
                    setError(t('qrScanner.cameraError'));
                    console.error(err);
                });
                
                return () => {
                    newScanner.destroy();
                };
            } catch (err) {
                setError(t('qrScanner.initError'));
                console.error(err);
            }
        }
        
        return () => {
            if (scanner) {
                scanner.destroy();
            }
        };
    }, [t]);
    
    const handleScan = (data: string) => {
        // Stop scanning once we get a result
        if (scanner) {
            scanner.stop();
        }
        
        // Process the QR code data
        try {
            // Check if the QR code data is a valid URL
            const url = new URL(data);
            
            // Expected format: timecapsule://exhibit/[exhibit-id]
            if (url.protocol === 'timecapsule:') {
                const pathParts = url.pathname.split('/').filter(Boolean);
                
                if (pathParts[0] === 'exhibit' && pathParts[1]) {
                    const exhibitId = pathParts[1] as ExhibitId;
                    
                    // Close the scanner in the navigation state
                    dispatch(closeScanner());
                    
                    // Check if this is a zone-based exhibit that needs automatic zone claiming
                    const zoneBasedExhibits = ['orang-laut', 'market-traders'];
                    const isZoneBased = zoneBasedExhibits.includes(exhibitId);
                    
                    if (isZoneBased) {
                        // For zone-based exhibits, navigate with fromQrScan parameter and timestamp
                        // The timestamp ensures the useEffect triggers even for repeated scans of the same QR code
                        const timestamp = Date.now().toString();
                        navigate(`/exhibit/${exhibitId}?fromQrScan=true&qrTimestamp=${timestamp}`);
                    } else {
                        // For non-zone exhibits, navigate normally
                        navigate(`/exhibit/${exhibitId}`);
                    }
                    return;
                }
            }
            
            // If we get here, it's not a valid Time Capsule QR code
            setError(t('qrScanner.invalidQrCode'));
            
            // Restart scanning after a delay
            setTimeout(() => {
                if (scanner) {
                    scanner.start();
                }
            }, 3000);
            
        } catch (err) {
            setError(t('qrScanner.invalidFormat'));
            console.error(err);
            
            // Restart scanning after a delay
            setTimeout(() => {
                if (scanner) {
                    scanner.start();
                }
            }, 3000);
        }
    };
    
    return (
        <div className="p-6 max-w-md mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">{t('qrScanner.title')}</h2>
            
            {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                    {error}
                </div>
            )}
            
            <div className="relative mx-auto mb-6">
                <div className="w-64 h-64 mx-auto overflow-hidden rounded-lg border-2 border-blue-500">
                    <video 
                        ref={videoRef} 
                        className="w-full h-full object-cover"
                    />
                </div>
                
                {scanning && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-40 h-40 border-2 border-blue-500 opacity-70 animate-pulse"></div>
                    </div>
                )}
            </div>
            
            <p className="mb-6">
                {t('qrScanner.instructions')}
            </p>
            
            <button 
                onClick={() => navigate('/')}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded"
            >
                {t('common.cancel')}
            </button>
        </div>
    );
}
